import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { ProOnly } from '../label/ProOnly';
import { NekoIcon } from '../misc/Icon';
import { useClasses } from '../hooks';
import NekoBusyOverlay from '../common/NekoBusyOverlay';

const TabGroup = Styled.div`
  display: flex;

  .mwai-action {
    display: flex;
    justify-content: flex-end;
    flex: auto;
    align-items: center;
  }
`;

const Tab = Styled.button`
  border-radius: 8px 8px 0px 0px;
  border: 0;
  background-color: var(--block-background-disabled);
  color: rgb(255 255 255 / 65%);
  display: flex;
  align-items: center;

  cursor: pointer;
  margin: 0 5px 0 0;
  text-align: left;
  padding: 12px 15px 12px 15px;

  &:focus {
    outline: none;
  }

  &.active {
    background-color: var(--block-background);
    color: var(--neko-white);
  }

  &.disabled {
    cursor: default;
    display: inline-flex;
    padding-bottom: 7px;
  }

  &.hidden {
    display: none;
  }

  &.inversed {

    &.active {
      background-color: var(--neko-white);
      color: var(--content-font-color);
    }
  }
`;

const TabContent = Styled.div`
  background-color: var(--block-background);
  color: var(--block-font-color);
  display: none;
  padding: 10px;
  border-radius: 0px 0px 8px 8px;

  &.active {
    display: block;
  }

  &.inversed {
    background-color: var(--neko-white);
    color: var(--neko-black);
  }
`;

const getKeyOfTab = (child, i) => {
  const props = child.props;
  let key = "tab-" + (i + 1);
  if (child.key) {
    key = child.key;
  }
  else if (typeof props.title === 'string') {
    key = props.title.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fa5\u3040-\u309f\u30a0-\u30ff\u2e80-\u2eff\u31f0-\u31ff\u3200-\u32ff\u3400-\u4dbf\uf900-\ufaff ]/gi, '');
    key = key.replace(/ /g, '-');
  }
  return encodeURIComponent(key);
}

const randomHash = (length = 6) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let hash = '';
  for (let i = 0; i < length; i++) {
    hash += chars[Math.floor(Math.random() * chars.length)];
  }
  return hash;
}

const StyledNekoTabs = props => {
  const { inversed, children, action, ...rest } = props;

  const childrenProp = useMemo(() => children ? (children.length ? children.filter(x => !!x) : [children]) : [], [children]);

  const childrenKeys = useMemo(() => {
    const allKeys = [];
    return React.Children.map(childrenProp, (child, i) => {
      let key = getKeyOfTab(child, i);
      // Let's make sure the keys are uniques.
      if (allKeys.indexOf(key) !== -1) {
        console.log("NekoTabs: Duplicated key found!", key, child);
        key = key + '-' + randomHash();
      }
      allKeys.push(key);
      return key;
    });
  }, [childrenProp]);

  const tabAttributes = useMemo(() => {
    const allKeys = [];
    return React.Children.map(childrenProp, (child, i) => {
      const title = child.props.title || ("Untitled Tab " + (i + 1));
      let key = getKeyOfTab(child, i);
      // Let's make sure the keys are uniques.
      if (allKeys.indexOf(key) !== -1) {
        console.log("NekoTabs: Duplicated key found!", key, child);
        key = key + '-' + randomHash();
      }
      allKeys.push(key);
      const onClick = child.props.onClick ? child.props.onClick : null;
      const requirePro = !props.isPro && (child.props.requirePro || false);
      const hidden = child.props.hidden || false;
      const icon = child.props.icon || false;
      return { key, title, onClick, requirePro, hidden, icon };
    });
  }, [childrenProp, props.isPro]);

  const [ currentTab, setCurrentTab ] = useState(props.currentTab ?? childrenKeys[0]);
  const classNames = useClasses('neko-tabs', { inversed: inversed });

  useEffect(() => {
    if (props.currentTab) {
      setCurrentTab(props.currentTab)
    }
  }, [props.currentTab]);

  useLayoutEffect(() => {
    if (props.keepTabOnReload) {
      const url = new URL(window.location.href);
      const title = url.searchParams.get('nekoTab');
      if (!title) {
        setCurrentTab(childrenKeys[0]);
        if (props.callOnTabChangeFirst) {
          onTabClick(0, tabAttributes[0]);
        }
        return;
      }

      const searchTitle = decodeURIComponent(title);
      const index = childrenKeys.indexOf(searchTitle);
      if (index === -1) {
        setCurrentTab(childrenKeys[0]);
        if (props.callOnTabChangeFirst) {
          onTabClick(0, tabAttributes[0]);
        }
        return;
      }

      setCurrentTab(childrenKeys[index]);
      if (props.callOnTabChangeFirst) {
        onTabClick(index, tabAttributes[index]);
      }
      return;
    }

    if (props.callOnTabChangeFirst) {
      const index = childrenKeys.indexOf(currentTab);
      onTabClick(index, tabAttributes[index]);
    }
  }, []);

  const keepTab = useCallback((key) => {
    if (history.pushState && typeof key === 'string' ) {
      let searchParams = new URLSearchParams(window.location.search);
      searchParams.set('nekoTab', key);
      let newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + searchParams.toString();
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }, []);

  const onTabClick = useCallback((tabIndex, tabAttr, ev) => {
    if (tabAttr.requirePro) return;

    setCurrentTab(tabAttr.key);
    if (props.onChange) {
      props.onChange(tabIndex, tabAttr, ev);
    }
    if (props.keepTabOnReload) {
      keepTab(tabAttr.key);
    }
  }, [props, keepTab]);

  const childTabs = React.Children.map(childrenProp, (child, i) => {
    const isActive = currentTab === getKeyOfTab(child, i);
    const hidden = child.props.hidden;
    if (isActive && hidden) {
      setCurrentTab(childrenKeys[0]);
    }
    return React.cloneElement(child, { isActive: isActive && !hidden, inversed });
  });

  return (
    <div className={classNames} {...rest}>
      <TabGroup>
        {tabAttributes.map((attr, i) =>
          <Tab key={attr.key} onClick={(ev) => { onTabClick(i, attr, ev) }}
            className={`neko-tab-title ${attr.key === currentTab ? 'active' : ''} ${attr.requirePro ? 'disabled' : ''} ${attr.hidden ? `hidden` : ''} ${inversed ? `inversed` : ''}`}>
            {attr.icon && <NekoIcon icon={attr.icon} width={15} height={15}
              style={{ marginRight: '2px' }} raw
            />}
            {attr.title}<ProOnly className="inline" show={attr.requirePro}
              style={{ marginLeft: 10, marginRight: -5, top: -1 }}
            />
          </Tab>
        )}
        {!!action && <div className="mwai-action">{action}</div>}
      </TabGroup>
      {childTabs}
    </div>
  );
};


/**
 * @oaram {string} title
 * @param {bool} isActive
 */
const StyledNekoTab = props => {
  const { children, isActive, busy = false } = props;
  const classNames = useClasses('neko-tab-content', { active: isActive,
    inversed: props.inversed });

  return (
    <NekoBusyOverlay busy={busy}>
      <TabContent className={classNames}>
        {!!isActive && children}
      </TabContent>
    </NekoBusyOverlay>
  );
};

const NekoTabs = (props) => {
  return (<StyledNekoTabs {...props} />);
};

NekoTabs.propTypes = {
  isPro: PropTypes.bool,
  onChange: PropTypes.func,
  action: PropTypes.node,
  currentTab: PropTypes.string,
  keepTabOnReload: PropTypes.bool,
  callOnTabChangeFirst: PropTypes.bool,
};

NekoTabs.defaultProps = {
  isPro: false,
  onChange: undefined,
  action: undefined,
  currentTab: undefined,
  keepTabOnReload: undefined,
  callOnTabChangeFirst: undefined,
};

const NekoTab = (props) => {
  return (<StyledNekoTab {...props} />);
};

NekoTab.propTypes = {
  isActive: PropTypes.bool,
  requirePro: PropTypes.bool,
  title: PropTypes.string,
  icon: PropTypes.string,
};

NekoTab.defaultProps = {
  isActive: false,
  requirePro: false,
  title: undefined,
  icon: undefined,
};

export { NekoTab, NekoTabs };
